import type { CompanyRepository } from '@mk1/domain';
import {
  companySnapshotSchema,
  domainEventSchema,
  exportPreviewSchema,
  type ActorContext,
  type CompanySnapshot,
  type DomainEvent,
  type ExportPreview,
} from '@mk1/protocol';

interface SessionResponse {
  actor: ActorContext;
  csrf: string;
}

async function parseResponse<T>(response: Response): Promise<T> {
  const body = (await response.json()) as { error?: string } & T;
  if (!response.ok) throw new Error(body.error ?? `Local API returned ${response.status}.`);
  return body;
}

export class ApiCompanyRepository implements CompanyRepository {
  private csrf: string | null = null;
  private readonly listeners = new Set<(snapshot: CompanySnapshot) => void>();
  private eventSource: EventSource | null = null;
  private refreshPending = false;

  private ensureEventStream(): void {
    if (this.eventSource) return;
    this.eventSource = new EventSource('/api/events/stream', { withCredentials: true });
    this.eventSource.addEventListener('domain-event', () => {
      if (this.refreshPending) return;
      this.refreshPending = true;
      void this.getSnapshot()
        .then((snapshot) => this.listeners.forEach((listener) => listener(snapshot)))
        .finally(() => {
          this.refreshPending = false;
        });
    });
  }

  async session(): Promise<ActorContext | null> {
    const response = await fetch('/api/auth/session', { credentials: 'same-origin' });
    if (response.status === 401) return null;
    const session = await parseResponse<SessionResponse>(response);
    this.csrf = session.csrf;
    this.ensureEventStream();
    return session.actor;
  }

  async login(
    personId: 'HUM-001' | 'HUM-002',
    passphrase: string,
    totpCode?: string,
  ): Promise<ActorContext> {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ personId, passphrase, totpCode }),
    });
    const session = await parseResponse<SessionResponse>(response);
    this.csrf = session.csrf;
    this.ensureEventStream();
    return session.actor;
  }

  async logout(): Promise<void> {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
    this.csrf = null;
    this.eventSource?.close();
    this.eventSource = null;
  }

  async getSnapshot(): Promise<CompanySnapshot> {
    const response = await fetch('/api/snapshot', { credentials: 'same-origin' });
    return companySnapshotSchema.parse(await parseResponse<unknown>(response));
  }

  subscribe(listener: (snapshot: CompanySnapshot) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async revokeTool(personId: string): Promise<DomainEvent> {
    const snapshot = await this.getSnapshot();
    const grant = snapshot.grants.find((candidate) => candidate.personId === personId);
    if (!grant) throw new Error(`No durable grant found for ${personId}.`);
    const response = await fetch(`/api/grants/${encodeURIComponent(grant.id)}/revoke`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
        ...(this.csrf ? { 'x-mk1-csrf': this.csrf } : {}),
      },
      body: JSON.stringify({
        reason: 'CEO immediate tool revocation from the employee access explorer.',
      }),
    });
    const event = domainEventSchema.parse(await parseResponse<unknown>(response));
    const updated = await this.getSnapshot();
    this.listeners.forEach((listener) => listener(updated));
    return event;
  }

  async approveGate(gateId: string): Promise<DomainEvent> {
    return this.decideGate(gateId, 'APPROVE_ONCE', {
      reason: 'Exact payload reviewed and approved for one consumption.',
    });
  }

  async decideGate(
    gateId: string,
    decision: 'APPROVE_ONCE' | 'APPROVE_BOUNDED' | 'REJECT' | 'RETURN' | 'REVOKE',
    input: { reason: string; scope?: Record<string, unknown>; expiresAt?: string },
  ): Promise<DomainEvent> {
    const response = await fetch(`/api/access/gates/${encodeURIComponent(gateId)}/decision`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
        ...(this.csrf ? { 'x-mk1-csrf': this.csrf } : {}),
      },
      body: JSON.stringify({ decision, ...input }),
    });
    const event = domainEventSchema.parse(await parseResponse<unknown>(response));
    const updated = await this.getSnapshot();
    this.listeners.forEach((listener) => listener(updated));
    return event;
  }

  async revokeGrant(grantId: string, reason: string): Promise<DomainEvent> {
    return this.accessMutation(`/api/access/grants/${encodeURIComponent(grantId)}/revoke`, {
      reason,
    });
  }

  async revokeResource(resourceId: string, reason: string): Promise<DomainEvent> {
    return this.accessMutation(`/api/access/resources/${encodeURIComponent(resourceId)}/revoke`, {
      reason,
    });
  }

  async suspendPerson(personId: string, reason: string): Promise<DomainEvent> {
    return this.accessMutation(`/api/access/people/${encodeURIComponent(personId)}/suspend`, {
      reason,
    });
  }

  async pauseDepartment(departmentId: string, reason: string): Promise<DomainEvent> {
    return this.accessMutation(
      `/api/access/departments/${encodeURIComponent(departmentId)}/pause`,
      {
        reason,
      },
    );
  }

  async pauseCompany(reason: string): Promise<DomainEvent> {
    return this.accessMutation('/api/access/company/pause', { reason });
  }

  async resumeCompany(reason: string, incidentId: string): Promise<DomainEvent> {
    return this.accessMutation('/api/access/company/resume', { reason, incidentId });
  }

  private async accessMutation(path: string, body: Record<string, unknown>): Promise<DomainEvent> {
    const response = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
        ...(this.csrf ? { 'x-mk1-csrf': this.csrf } : {}),
      },
      body: JSON.stringify(body),
    });
    const event = domainEventSchema.parse(await parseResponse<unknown>(response));
    const updated = await this.getSnapshot();
    this.listeners.forEach((listener) => listener(updated));
    return event;
  }

  async acknowledgeNotification(id: string): Promise<DomainEvent> {
    const response = await fetch(`/api/notifications/${encodeURIComponent(id)}/acknowledge`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: this.csrf ? { 'x-mk1-csrf': this.csrf } : {},
    });
    const event = domainEventSchema.parse(await parseResponse<unknown>(response));
    const updated = await this.getSnapshot();
    this.listeners.forEach((listener) => listener(updated));
    return event;
  }

  async exportPreview(format: 'markdown' | 'json'): Promise<ExportPreview> {
    const response = await fetch(`/api/export-preview?format=${format}`, {
      credentials: 'same-origin',
    });
    return exportPreviewSchema.parse(await parseResponse<unknown>(response));
  }

  async runSyntheticCompanyDay(paceMs = 40): Promise<void> {
    const response = await fetch('/api/scenarios/company-day', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
        ...(this.csrf ? { 'x-mk1-csrf': this.csrf } : {}),
      },
      body: JSON.stringify({ paceMs }),
    });
    await parseResponse<unknown>(response);
    const updated = await this.getSnapshot();
    this.listeners.forEach((listener) => listener(updated));
  }

  async setSimulationPause(
    scopeType: 'PERSON' | 'DEPARTMENT' | 'COMPANY',
    scopeId: string,
    paused: boolean,
    reason: string,
  ): Promise<void> {
    const response = await fetch('/api/operations/pause', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
        ...(this.csrf ? { 'x-mk1-csrf': this.csrf } : {}),
      },
      body: JSON.stringify({
        id: `PAUSE-UI-${scopeType}-${scopeId}`,
        scopeType,
        scopeId,
        paused,
        reason,
      }),
    });
    await parseResponse<unknown>(response);
    const updated = await this.getSnapshot();
    this.listeners.forEach((listener) => listener(updated));
  }

  async controlRunnerNode(
    nodeId: string,
    control: 'DRAIN' | 'QUARANTINE' | 'REVOKE',
    reason: string,
  ): Promise<DomainEvent> {
    const response = await fetch(`/api/runner-grid/nodes/${encodeURIComponent(nodeId)}/control`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
        ...(this.csrf ? { 'x-mk1-csrf': this.csrf } : {}),
      },
      body: JSON.stringify({ control, reason }),
    });
    const event = domainEventSchema.parse(await parseResponse<unknown>(response));
    const updated = await this.getSnapshot();
    this.listeners.forEach((listener) => listener(updated));
    return event;
  }

  async recordCapacityObservation(input: {
    poolId: string;
    status: 'AVAILABLE' | 'LOW' | 'EXHAUSTED' | 'COOLDOWN' | 'AUTH_REQUIRED';
    remainingValue: number | null;
    remainingUnit: 'MESSAGES' | 'CREDITS' | 'PERCENT' | 'UNKNOWN';
    resetsAt: string | null;
    weeklyResetsAt: string | null;
  }): Promise<DomainEvent> {
    const response = await fetch(
      `/api/runner-grid/pools/${encodeURIComponent(input.poolId)}/observation`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'content-type': 'application/json',
          ...(this.csrf ? { 'x-mk1-csrf': this.csrf } : {}),
        },
        body: JSON.stringify({
          ...input,
          source: 'OPERATOR',
          confidence: 'OPERATOR_CONFIRMED',
        }),
      },
    );
    const event = domainEventSchema.parse(await parseResponse<unknown>(response));
    const updated = await this.getSnapshot();
    this.listeners.forEach((listener) => listener(updated));
    return event;
  }
}
